import React, { useEffect } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import Tooltip from '@material-ui/core/Tooltip';

import { openOrCloseGlobalDialog } from '../action/dialog/global-dialog';
import { loadHrisImportLogs } from '../hris-import-log/actions/load-hris-import-logs';
import { hrisImportLogForMergeIntegrationIdSelector } from '../hris-import-log/hris-import-log-selectors';

import { GLOBAL_DIALOGS } from '../dialogs/global-dialogs-config';
import { UiButton } from '../ui/ui-button';

import { MergeIntegrationSyncHealthIndicator } from './merge-integration-sync-health-indicator';

import { getHrisDisplayName } from './util/merge-display-utils';
import { MERGE_SYNC_STATUS } from './util/merge-sync-status';
import { formatRelativeDate } from '../util/formatters';
import { sortByDate } from '../util/sort-functions';

type InputProps = {
  className?: string;
  org: Org;
  mergeIntegration?: MergeIntegration;
  hrisIntegrationResponse?: HrisIntegrationResponse;
  buttonSize?: 'default' | 'xxs' | 'xs' | 'sm' | 'lg';
};

export const MergeIntegrationDetailsDialogButton = ({
  className,
  org,
  mergeIntegration,
  hrisIntegrationResponse,
  buttonSize,
}: InputProps) => {
  const dispatch = useDispatch();
  const orgIdFilter = org.org_id !== mergeIntegration?.orgId ? org.org_id : null;
  useEffect(() => {
    if (mergeIntegration?.mergeIntegrationId) {
      dispatch(loadHrisImportLogs(mergeIntegration?.mergeIntegrationId));
    }
  }, [mergeIntegration?.mergeIntegrationId, orgIdFilter, dispatch]);

  const hrisImportLogs = useSelector((state: any) =>
    mergeIntegration?.mergeIntegrationId
      ? hrisImportLogForMergeIntegrationIdSelector(state, mergeIntegration?.mergeIntegrationId)
      : null,
  );

  if (!mergeIntegration) {
    return null;
  }
  const sortedHrisImportLogs = (hrisImportLogs || []).sort(sortByDate((log) => log.createdAt));

  const handleDialogOpenClick = () => {
    dispatch(
      openOrCloseGlobalDialog(GLOBAL_DIALOGS.MERGE_INTEGRATION_DETAILS_DIALOG.key, true, {
        org,
        onClose: handleDialogCloseClick,
      }),
    );
  };

  const handleDialogCloseClick = () => {
    dispatch(openOrCloseGlobalDialog(GLOBAL_DIALOGS.MERGE_INTEGRATION_DETAILS_DIALOG.key, false));
  };

  const isPaused = mergeIntegration.syncStatus === MERGE_SYNC_STATUS.IMPORT_PAUSED;
  const lastSyncDate = sortedHrisImportLogs?.length && sortedHrisImportLogs[0].finishedAt;
  const hrisDisplayName = getHrisDisplayName(mergeIntegration, hrisIntegrationResponse);

  return (
    <Tooltip title={isPaused ? 'Integration paused' : ''}>
      <div>
        <UiButton
          variant="secondary"
          className={className}
          onClick={handleDialogOpenClick}
          disabled={isPaused}
          size={buttonSize}
        >
          <div className="u-display-flex">
            <MergeIntegrationSyncHealthIndicator
              orgId={org.org_id}
              mergeOriginOrgId={mergeIntegration.orgId}
              accountId={org.accountId}
              showTooltip
            >
              <div className="u-display-flex u-flexDirection-column">
                Syncing with {hrisDisplayName}{' '}
                <div className="u-fontSize-0_7em u-color-gray">
                  Last sync: {lastSyncDate ? formatRelativeDate(lastSyncDate) : 'N/A'}
                </div>
              </div>
            </MergeIntegrationSyncHealthIndicator>
          </div>
        </UiButton>
      </div>
    </Tooltip>
  );
};
